// @ts-strict-ignore
import axios from 'axios'
import axiosRetry, { isRetryableError } from 'axios-retry'
import queryString from 'query-string'
import { BASE_PATH } from 'constants/basePath'
import {
  DEFAULT_GRAPH_VERSION,
  FAROS_GRAPHQL_VERSION_HEADER,
} from 'constants/common'
import ROUTES from 'constants/routes'
import { delay } from 'utils/promise'
import { forwardTracingHeaders } from 'utils/tracing'
import { isMaintenanceEnabled } from './utils'

const DEFAULT_RETRIES = 3
const DEFAULT_RETRY_DELAY = 1000

type StringifyParams = Parameters<typeof queryString.stringify>

export { BASE_PATH }
const baseURL = BASE_PATH + ROUTES.API.FAROS_API_PROXY
const instance = axios.create({
  baseURL,
  // use `?id=1&id=2&id=3` format instead of `?id[]=1&id[]=2&id[]=3`
  paramsSerializer: (params: StringifyParams) =>
    queryString.stringify(params, { arrayFormat: 'none' }),
})

instance.interceptors.request.use((config) => {
  // Uncomment to debug requests:
  // console.log('Starting Request', JSON.stringify(config, null, 2))

  // If config includes current `graph`, pass it to the HTTP header
  if (config.graph) {
    // eslint-disable-next-line
    config.headers[FAROS_GRAPHQL_VERSION_HEADER] = DEFAULT_GRAPH_VERSION
  }

  return forwardTracingHeaders(config)
})

instance.interceptors.response.use(
  (response) => {
    // Uncomment to debug responses:
    // console.log('Response:', JSON.stringify(response, null, 2))

    if (typeof window !== 'undefined' && isMaintenanceEnabled(response)) {
      // When the maintenance mode is detected, initiate page reload
      // and wait (if we don't wait, error screen would be displayed
      // for a short moment).
      location.reload()
      return delay(1000)
    }
    return response.data // eslint-disable-line @typescript-eslint/no-unsafe-return
  },
  (error) => {
    if (typeof window !== 'undefined' && axios.isAxiosError(error)) {
      // Remove unused properties from the error object
      // so they won't be visible in the browser console.
      // See https://faros-ai.atlassian.net/browse/FAI-2879

      /* eslint-disable no-param-reassign */
      delete error.config
      delete error.request
      delete error.response?.config
      delete error.response?.request
      /* eslint-enable no-param-reassign */
    }
    return Promise.reject(error)
  }
)

if (typeof window === 'undefined') {
  // Add retry mechanism for requests called from API routes.
  // This will not apply to client side (browser) since there
  // already is retry mechanism provided by `react-query` lib.
  axiosRetry(instance, {
    retries: DEFAULT_RETRIES,
    retryCondition: isRetryableError, // retry on 5xx response status code
    retryDelay: (retryNumber) => {
      return retryNumber * DEFAULT_RETRY_DELAY
    },
  })
}

export { instance as farosApi }

/**
 * Type-safe wrapper for farosApi.get that properly types the response.
 * The farosApi interceptor returns response.data directly, so this wrapper
 * ensures TypeScript knows the response is the data type, not AxiosResponse.
 */
export async function farosApiGet<T>(
  url: string,
  config?: Parameters<typeof instance.get>[1]
): Promise<T> {
  const response = await instance.get(url, config)
  return response as T
}
